import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-20888ce9"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "input-container" }
const _hoisted_2 = { class: "drill-hole-inputs" }

import {computed, onBeforeMount, onBeforeUnmount} from "vue";
import { useStore } from "vuex";
import {addSizeToDataLayer} from "@/functions/analytics";
import {stripNonNumeric, endsWithSeparator, limitDecimals, toNumber} from "@/functions/numbers";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import DrillHoleConfigurationFactory from "@/Models/DrillHoleConfigurations/DrillHoleConfigurationFactory";
import Length from "../../../source/js/shared/models/Geometry/Length";
import ValidationMessage from "./UI/ValidationMessage.vue";
import MessageContainer from "@/components/UI/MessageContainer.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'DrillHoleRadialSettings',
  setup(__props) {

const store = useStore();

const enclosingRectangle = computed(() => store.getters.enclosingRectangle );
const isDrillHoleValid = computed(() => store.getters.isDrillHoleValid );
const getDrillHoleValidationMessage = computed(() => store.getters.getDrillHoleValidationMessage );
const minimumDrillHoleDiameter = computed(() => store.getters.minimumDrillHoleDiameter );
const lastGeneratedConfiguration = computed(() => store.getters.lastGeneratedConfiguration );

const drillOption = computed(() => store.getters.drillOption);
const drillHoleRadialAmount = computed({
	get: () => store.getters.drillHoleRadialAmount,
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit('updateDrillHoleRadialAmount', toNumber(stripNonNumeric(value)));
			store.commit('updateUsingDefaultDrillHoleDimensions', false);
		}
	}
});

const drillHoleDiameter = computed({
	get: () => store.getters.drillHoleDiameter.in_cm(),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit('updateDrillHoleDiameter', Length.from_cm( limitDecimals( stripNonNumeric(value), 1 ) ).in_mm() );
			store.commit('updateUsingDefaultDrillHoleDimensions', false);
		}
	}
});

const drillHoleRadialDiameter = computed({
	get: () => store.getters.drillHoleRadialDiameter.round( 0 ).in_cm(),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit('updateDrillHoleRadialDiameter', Length.from_cm( limitDecimals( stripNonNumeric(value), 1 ) ).in_mm() );
			store.commit('updateUsingDefaultDrillHoleDimensions', false);
		}
	}
});

const drillHoleRadialOffsetX = computed({
	get: () => store.getters.drillHoleRadialOffsetX.round( 0 ).in_cm(),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit('updateDrillHoleRadialOffsetX', Length.from_cm( limitDecimals( stripNonNumeric(value), 1 ) ).in_mm() );
			store.commit('updateUsingDefaultDrillHoleDimensions', false);
		}
	}
});

const drillHoleRadialOffsetY = computed({
	get: () => store.getters.drillHoleRadialOffsetY.round( 0 ).in_cm(),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit('updateDrillHoleRadialOffsetY', Length.from_cm( limitDecimals( stripNonNumeric(value), 1 ) ).in_mm() );
			store.commit('updateUsingDefaultDrillHoleDimensions', false);
		}
	}
});
const getCenter = computed(() => store.getters.getCenter).value;
document.addEventListener("focusin", focusChanged);

function focusChanged(event): void {
	store.commit("updateFocusedElement", event.target);
}

function drillHoleFieldsWithMessage(): {key: string, message: string}[] {
	const configuration = new DrillHoleConfigurationFactory().get_configuration( 'radial' );

	// filter out keys with valid dimensions
	const invalidKeys = configuration.get_input_keys().filter((field) => !isDrillHoleValid.value(field));

	// get the data we need to render a notice
	return invalidKeys.map((field) => {
		return {
			key: field,
			message: getDrillHoleValidationMessage.value(field),
		};
	});
}

onBeforeMount(async () => {

	// Don't set defaults if we have an old configuration available for radial drill holes.
	if ( lastGeneratedConfiguration.value.drillholes?.type === 'radial' ) {
		return;
	}

	const radialDiameter = Length.from_cm( enclosingRectangle.value.width ).min( Length.from_cm( enclosingRectangle.value.height ) ).subtract( new Length( 30 ) );
	const center = getCenter(true, 'mm');

	await store.dispatch( 'updateDrillHolesFromConfig', {
		type: 'radial',
		diameter: Length.from_cm( drillHoleDiameter.value ).round( 1 ).in_mm(),
		amount: drillHoleRadialAmount.value,
		position: {
			left: center.x,
			bottom: center.y,
		},
		radialDiameter: radialDiameter.divide( 2 ).round( 0 ).in_mm()
	} );
});

onBeforeUnmount(() => {
	document.removeEventListener("focusin", focusChanged);
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(DimensionInput, {
        inputRef: "drillHoleRadialAmount",
        id: "drillHoleRadialAmount",
        name: "drillHoleRadialAmount",
        inputmode: "numeric",
        min: 1,
        label: _ctx.$i18n.drillHoleConfiguration.radial.amount,
        invalid: !isDrillHoleValid.value('drillHoleRadialAmount'),
        onBlur: () => _unref(addSizeToDataLayer)(_ctx.$i18n.drillHoleConfiguration.radial.amount, drillHoleRadialAmount.value, 'radial-drill-hole'),
        modelValue: drillHoleRadialAmount.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((drillHoleRadialAmount).value = $event)),
        step: 1
      }, null, 8, ["label", "invalid", "onBlur", "modelValue"]),
      _createVNode(DimensionInput, {
        inputRef: "drillHoleDiameter",
        id: "drillHoleDiameter",
        name: "drillHoleDiameter",
        unit: "cm",
        min: minimumDrillHoleDiameter.value,
        label: `${_ctx.$i18n.drillHoleConfiguration.diameter_drill_hole}`,
        invalid: !isDrillHoleValid.value('drillHoleDiameter'),
        onBlur: () => _unref(addSizeToDataLayer)(_ctx.$i18n.drillHoleConfiguration.diameter_drill_hole, drillHoleDiameter.value, 'radial-drill-hole'),
        modelValue: drillHoleDiameter.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((drillHoleDiameter).value = $event))
      }, null, 8, ["min", "label", "invalid", "onBlur", "modelValue"]),
      _createVNode(DimensionInput, {
        inputRef: "drillHoleRadialDiameter",
        id: "drillHoleRadialDiameter",
        name: "drillHoleRadialDiameter",
        unit: "cm",
        label: _ctx.$i18n.drillHoleConfiguration.radial.diameter_from_center,
        invalid: !isDrillHoleValid.value('drillHoleRadialDiameter'),
        onBlur: () => _unref(addSizeToDataLayer)(_ctx.$i18n.drillHoleConfiguration.radial.diameter_from_center, drillHoleRadialDiameter.value, 'radial-drill-hole'),
        modelValue: drillHoleRadialDiameter.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((drillHoleRadialDiameter).value = $event))
      }, null, 8, ["label", "invalid", "onBlur", "modelValue"]),
      _createVNode(DimensionInput, {
        inputRef: "drillHoleRadialOffsetX",
        id: "drillHoleRadialOffsetX",
        name: "drillHoleRadialOffsetX",
        unit: "cm",
        label: _ctx.$i18n.drillHoleConfiguration.radial.offset_x,
        invalid: !isDrillHoleValid.value('drillHoleRadialOffsetX'),
        onBlur: () => _unref(addSizeToDataLayer)(_ctx.$i18n.drillHoleConfiguration.radial.offset_x, drillHoleRadialOffsetX.value, 'radial-drill-hole'),
        modelValue: drillHoleRadialOffsetX.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((drillHoleRadialOffsetX).value = $event)),
        class: "half-width"
      }, null, 8, ["label", "invalid", "onBlur", "modelValue"]),
      _createVNode(DimensionInput, {
        inputRef: "drillHoleRadialOffsetY",
        id: "drillHoleRadialOffsetY",
        name: "drillHoleRadialOffsetY",
        unit: "cm",
        label: _ctx.$i18n.drillHoleConfiguration.radial.offset_y,
        invalid: !isDrillHoleValid.value('drillHoleRadialOffsetY'),
        onBlur: () => _unref(addSizeToDataLayer)(_ctx.$i18n.drillHoleConfiguration.radial.offset_y, drillHoleRadialOffsetY.value, 'radial-drill-hole'),
        modelValue: drillHoleRadialOffsetY.value,
        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((drillHoleRadialOffsetY).value = $event)),
        class: "half-width"
      }, null, 8, ["label", "invalid", "onBlur", "modelValue"])
    ]),
    _createVNode(MessageContainer, null, {
      default: _withCtx(() => [
        (drillOption.value !== 'none')
          ? (_openBlock(), _createBlock(ValidationMessage, {
              key: 0,
              type: "notice"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$i18n.drillHolesNotice), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(drillHoleFieldsWithMessage(), (field) => {
          return (_openBlock(), _createBlock(ValidationMessage, {
            key: field.key + '-validation-message',
            "input-id": field.key
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(field.message), 1)
            ]),
            _: 2
          }, 1032, ["input-id"]))
        }), 128)),
        (! isDrillHoleValid.value('drillHolesMissing'))
          ? (_openBlock(), _createBlock(ValidationMessage, { key: 1 }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(getDrillHoleValidationMessage.value('drillHolesMissing')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})